import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { api, CATEGORIES, fmtDate } from "@/lib/api";
import { ArticleCard, ArticleMeta, AccessBadge, SponsorBadge } from "@/components/ArticleCard";
import { ReportCard } from "@/components/ReportCard";
import { NewsletterForm } from "@/components/Footer";

const Hero = ({ a }) => (
  <Link to={`/article/${a.slug}`} data-testid="hero-featured-story" className="group block lg:col-span-8 fade-up">
    <div className="img-zoom overflow-hidden aspect-[2/1] bg-[#E7E7E7]"><img src={a.image} alt={a.title} className="w-full h-full object-cover" /></div>
    <div className="pt-6">
      <div className="flex items-center gap-3"><ArticleMeta a={a} /><AccessBadge access={a.access} /></div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mt-4 group-hover:text-[#E5332A] transition-colors">{a.title}</h1>
      <p className="text-base md:text-lg text-[#575757] mt-5 max-w-3xl leading-relaxed">{a.excerpt}</p>
      <div className="flex flex-wrap items-center gap-4 mt-5 text-sm text-[#575757]">
        <span className="text-[#0A0A0A] font-medium">{a.author}</span><span>{a.read_time} min read</span>
        {a.takeaways?.length > 0 && <span className="flex items-center gap-1 text-[#E5332A]"><Sparkles size={14} /> AI key takeaways</span>}
        {a.sponsor_name && <SponsorBadge name={a.sponsor_name} label="Intelligence partner:" />}
      </div>
    </div>
  </Link>
);

const BriefsRail = ({ items }) => (
  <aside className="lg:col-span-4 lg:border-l lg:border-[#E7E7E7] lg:pl-8" data-testid="daily-briefs-rail">
    <div className="flex items-center justify-between border-b-2 border-[#0A0A0A] pb-3">
      <h2 className="font-display text-2xl font-bold">Daily Briefs</h2>
      <span className="overline text-[#575757]">{fmtDate(new Date())}</span>
    </div>
    {items.map((a, i) => <ArticleCard key={a.slug} a={a} variant="compact" index={i} />)}
    <Link to="/news?type=brief" data-testid="all-briefs-link" className="mt-5 inline-flex items-center gap-2 text-sm text-[#E5332A] link-underline">All daily briefs <ArrowRight size={14} /></Link>
    <div className="mt-10 bg-[#0A0A0A] text-white p-6 relative grain">
      <p className="overline text-[#E5332A] relative">The Tuesday Roast</p>
      <p className="font-display text-xl mt-2 relative">Africa's markets, deals and commodity prices, in your inbox every Tuesday at 5am.</p>
      <div className="mt-5 relative"><NewsletterForm dark testid="rail-newsletter" /></div>
    </div>
  </aside>
);

const SectionHead = ({ over, title, to, linkLabel }) => (
  <div className="flex items-end justify-between border-b border-[#E7E7E7] pb-4 mb-10">
    <div><p className="overline text-[#E5332A]">{over}</p><h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold mt-2">{title}</h2></div>
    {to && <Link to={to} className="text-sm text-[#E5332A] link-underline hidden sm:inline">{linkLabel}</Link>}
  </div>
);

export default function Home() {
  const [articles, setArticles] = useState([]);
  const [reports, setReports] = useState([]);
  useEffect(() => {
    api.get("/articles").then((r) => setArticles(r.data));
    api.get("/reports").then((r) => setReports(r.data));
  }, []);
  if (!articles.length) return <div className="min-h-[60vh]" />;
  const featured = articles.find((a) => a.featured) || articles[0];
  const briefs = articles.filter((a) => a.type === "brief").slice(0, 4);
  const analysis = articles.filter((a) => a.type !== "brief" && a.slug !== featured.slug).slice(0, 6);

  return (
    <div data-testid="home-page">
      <section className="max-w-7xl mx-auto px-5 lg:px-10 pt-10 lg:pt-14 grid lg:grid-cols-12 gap-10">
        <Hero a={featured} />
        <BriefsRail items={briefs} />
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-16">
        <div className="flex flex-wrap gap-2" data-testid="sector-chips">
          {CATEGORIES.map((c) => (
            <Link key={c} to={`/news?category=${encodeURIComponent(c)}`} className="shrink-0 border border-[#E7E7E7] px-4 py-2 text-xs font-mono-d uppercase tracking-wider hover:bg-[#0A0A0A] hover:text-white transition-colors">{c}</Link>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
        <SectionHead over="Weekly analysis" title="Context the market misses" to="/news?type=analysis" linkLabel="All analysis" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
          {analysis.map((a, i) => <ArticleCard key={a.slug} a={a} index={i} />)}
        </div>
      </section>

      <section className="bg-[#0A0A0A] relative grain">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24 relative">
          <div className="grid lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-5">
              <p className="overline text-[#E5332A]">Research & sector reports</p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mt-3 leading-tight">Decision-grade intelligence on Africa's most consequential sectors.</h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 text-[#E7E7E7]/75 text-sm leading-relaxed self-end">
              Purchase individual reports, or unlock the complete library with an Institutional membership. Partners can co-publish research and reach our senior reader base.
              <Link to="/research" data-testid="home-research-link" className="mt-4 flex items-center gap-2 text-[#E5332A] link-underline w-fit">Browse the research store <ArrowRight size={14} /></Link>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {reports.map((r, i) => <ReportCard key={r.slug} r={r} dark index={i} />)}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <p className="overline text-[#E5332A]">Membership</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold mt-3 leading-tight">Built for people who refuse to operate on assumption.</h2>
          <p className="text-[#575757] mt-5 max-w-xl leading-relaxed">Trade advisors, commodity buyers, impact investors and agribusiness founders rely on Africa Brew Brief for the depth mainstream media fails to provide. Choose monthly or annual — save two months when you pay yearly.</p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/pricing" data-testid="home-pricing-cta" className="bg-[#E5332A] text-white px-6 py-3 text-sm hover:bg-[#B8241C] transition-colors">View membership plans</Link>
            <Link to="/sponsor" data-testid="home-sponsor-cta" className="border border-[#0A0A0A] px-6 py-3 text-sm hover:bg-[#0A0A0A] hover:text-white transition-colors">Sponsor an article</Link>
          </div>
        </div>
        <div className="lg:col-span-4 lg:col-start-9 grid grid-cols-2 gap-px bg-[#E7E7E7] border border-[#E7E7E7]">
          {[["54", "economies covered"], ["7", "core sectors"], ["Daily", "news briefs"], ["Weekly", "deep analysis"]].map(([n, l]) => (
            <div key={l} className="bg-[#FFFFFF] p-6"><p className="font-display text-3xl font-bold text-[#E5332A]">{n}</p><p className="text-xs text-[#575757] mt-1 uppercase tracking-wider">{l}</p></div>
          ))}
        </div>
      </section>
    </div>
  );
}
